// DataTable cell renderer — extracted from DataTable.tsx (CHG-1225) to keep
// DataTable itself ≤150 LOC per PM's slim-primitive target.
//
// Adding a new column type:
//   1. Add to DataTableColumnType union in DataTable.tsx
//   2. Add a case to renderCell() below
//   3. (optional) Add column-config field to DataTableColumn interface

import type { ReactNode } from "react";
import { useFormatters } from "../../contexts/IntlContext";
import { StageChip } from "./StageChip";
import type { DataTableColumn } from "./DataTable";

// CHG-1666 / ITER-3778 — semantic tone map for the lifecycle-badge cell.
// Drives both the lifecycle_stage column and any future entity that
// reuses the lifecycle-state vocabulary. Add new stages here when a
// matching value-space ships.
const LIFECYCLE_TONE: Record<string, { bg: string; fg: string; label?: string }> = {
  active:       { bg: "var(--color-success-tint)", fg: "var(--color-success)", label: "Active" },
  recurring:    { bg: "var(--color-success-tint)", fg: "var(--color-success)", label: "Recurring" },
  first_time:   { bg: "var(--color-info-tint)",    fg: "var(--color-info)",    label: "First-time" },
  reactivated:  { bg: "var(--color-info-tint)",    fg: "var(--color-info)",    label: "Reactivated" },
  prospect:     { bg: "var(--color-info-tint)",    fg: "var(--color-info)",    label: "Prospect" },
  at_risk:      { bg: "var(--color-warning-tint)", fg: "var(--color-warning)", label: "At risk" },
  failed:       { bg: "var(--color-error-tint)",   fg: "var(--color-error)",   label: "Failed" },
  lapsed:       { bg: "var(--color-error-tint)",   fg: "var(--color-error)",   label: "Lapsed" },
  // composition-lint-exempt: VIP purple has no semantic theme token — intentional brand accent (DRIFT-776)
  vip:          { bg: "rgba(168, 85, 247, 0.14)", fg: "rgb(107, 33, 168)", label: "VIP" },
};
const LIFECYCLE_NEUTRAL = { bg: "var(--color-bg-subtle)", fg: "var(--color-text-secondary)" };

// Deterministic palette for avatar initial circles. Hash the source string
// into one of N tones so the same donor always gets the same color.
const AVATAR_PALETTE = [
  // composition-lint-exempt: deterministic non-semantic avatar-initial hue palette — hashed per donor, not status colors (DRIFT-776)
  { bg: "rgb(219, 234, 254)", fg: "rgb(30, 64, 175)" },   // blue
  { bg: "rgb(220, 252, 231)", fg: "rgb(22, 101, 52)" },   // green
  { bg: "rgb(254, 226, 226)", fg: "rgb(153, 27, 27)" },   // red
  { bg: "rgb(255, 237, 213)", fg: "rgb(154, 52, 18)" },   // orange
  { bg: "rgb(243, 232, 255)", fg: "rgb(107, 33, 168)" },  // purple
  // composition-lint-exempt: deterministic non-semantic avatar-initial hue palette, continued (DRIFT-776)
  { bg: "rgb(207, 250, 254)", fg: "rgb(14, 116, 144)" },  // cyan
  { bg: "rgb(254, 240, 138)", fg: "rgb(133, 77, 14)" },   // yellow
  { bg: "rgb(252, 231, 243)", fg: "rgb(157, 23, 77)" },   // pink
];

function hashCode(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = ((h << 5) - h + s.charCodeAt(i)) | 0;
  return Math.abs(h);
}

function initialsOf(name: string): string {
  const cleaned = name.trim();
  if (!cleaned) return "?";
  const parts = cleaned.split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function renderAvatarName(name: string): ReactNode {
  const palette = AVATAR_PALETTE[hashCode(name) % AVATAR_PALETTE.length];
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
      <span
        aria-hidden="true"
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: 28,
          height: 28,
          borderRadius: "50%",
          background: palette.bg,
          color: palette.fg,
          fontSize: 11,
          fontWeight: 600,
          flexShrink: 0,
        }}
      >
        {initialsOf(name)}
      </span>
      <span>{name}</span>
    </span>
  );
}

function renderLifecycleBadge(value: string): ReactNode {
  const tone = LIFECYCLE_TONE[value] ?? LIFECYCLE_NEUTRAL;
  const label = "label" in tone && tone.label ? tone.label : value.replace(/_/g, " ");
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        height: 22,
        padding: "0 8px",
        fontSize: 11,
        fontWeight: 600,
        background: tone.bg,
        color: tone.fg,
        borderRadius: 9999,
        textTransform: "capitalize",
      }}
    >
      {label}
    </span>
  );
}

function renderChipList(raw: unknown): ReactNode {
  const arr: string[] = Array.isArray(raw)
    ? raw.map((v) => String(v)).filter((v) => v.length > 0)
    : [];
  if (arr.length === 0) {
    return <span style={{ color: "var(--color-text-subtle)" }}>—</span>;
  }
  const visible = arr.slice(0, 3);
  const overflow = arr.length - visible.length;
  return (
    <span style={{ display: "inline-flex", flexWrap: "wrap", gap: 4 }}>
      {visible.map((t) => (
        <span
          key={t}
          style={{
            display: "inline-flex",
            alignItems: "center",
            height: 20,
            padding: "0 8px",
            fontSize: 11,
            fontWeight: 500,
            background: "var(--color-bg-subtle)",
            color: "var(--color-text-secondary)",
            border: "1px solid var(--color-border-subtle)",
            borderRadius: 9999,
          }}
        >
          {t}
        </span>
      ))}
      {overflow > 0 ? (
        <span style={{ fontSize: 11, color: "var(--color-text-muted)", alignSelf: "center" }}>
          +{overflow} more
        </span>
      ) : null}
    </span>
  );
}

export function renderCell<T>(
  c: DataTableColumn<T>,
  row: T,
  fmt: ReturnType<typeof useFormatters>,
): ReactNode {
  if (c.type === "custom" && c.render) return c.render(row);
  const raw = (row as Record<string, unknown>)[c.field];
  // CHG-1666 — chip-list handles its own empty case (renders "—" when
  // the array is empty); the early-null short-circuit below would
  // otherwise mask the array path entirely.
  if (c.type !== "chip-list" && (raw === null || raw === undefined)) {
    return <span style={{ color: "var(--color-text-subtle)" }}>—</span>;
  }
  switch (c.type) {
    case "currency": {
      const currency = c.currencyField
        ? String((row as Record<string, unknown>)[c.currencyField] ?? "USD")
        : "USD";
      return <span className="tabular">{fmt.formatCurrency(Number(raw), currency)}</span>;
    }
    case "number":
      return <span className="tabular">{fmt.formatNumber(Number(raw))}</span>;
    case "date":
      return fmt.formatDate(String(raw), "medium");
    case "datetime": {
      // CHG-1354 / Proposal 3 — relative-time label with absolute date as
      // tooltip. Mirrors v1 web/dashboard/src/components/data/DataTable.tsx
      // datetime treatment (distinct from `date` which is timestamp-coarse).
      // The native <time> element gives assistive tech an ISO anchor.
      const v = String(raw);
      return (
        <time dateTime={v} title={fmt.formatDate(v, "medium")}>
          {fmt.formatDate(v, "relative")}
        </time>
      );
    }
    case "relative-date":
      return fmt.formatDate(String(raw), "relative");
    case "email":
      return (
        <a href={`mailto:${raw}`} style={{ color: "var(--color-primary)" }}>
          {String(raw)}
        </a>
      );
    case "stage-chip":
      return c.entity ? <StageChip stage_id={String(raw)} entity={c.entity} size="sm" /> : String(raw);
    case "avatar-name":
      return renderAvatarName(String(raw));
    case "lifecycle-badge":
      return renderLifecycleBadge(String(raw));
    case "chip-list":
      return renderChipList(raw);
    case "actions":
      return (
        <div style={{ display: "flex", gap: 6 }}>
          {(c.actions ?? []).map((a) => (
            <button
              key={a.id}
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={(e) => {
                e.stopPropagation();
                a.onClick(row);
              }}
            >
              {a.label}
            </button>
          ))}
        </div>
      );
    case "text":
    default:
      return String(raw);
  }
}
